(() => {
  'use strict';

  const consentSelector = '#shopify-pc__banner, #shopify-pc__prefs__dialog, [id^="shopify-pc__"][role="dialog"], [id^="shopify-pc__"][role="alertdialog"]';
  const formSelector = '[data-testid^="klaviyo-form-"]';
  const dialogSelector = '[role="dialog"]';
  const chatSelector = 'shopify-chat, shopify-agent';
  const suppressedClass = 'kk-klaviyo-suppressed';
  const suppressedChatClass = 'kk-shopify-chat-suppressed';
  const bodyLockClass = 'klaviyo-prevent-body-scrolling';
  const releaseDelay = 8000;

  const trackedDialogs = new Set();
  const trackedChatHosts = new Set();
  let phase = 'initial';
  let releaseTimer = null;
  let restoreBodyLock = false;
  let reconcileQueued = false;

  const isVisible = (element) => {
    if (!element || !element.isConnected || element.hidden) return false;
    if ((element.getAttribute('aria-hidden') || '').toLowerCase() === 'true') return false;

    const styles = window.getComputedStyle(element);
    if (styles.display === 'none') return false;
    if (styles.visibility === 'hidden' || styles.visibility === 'collapse') return false;
    if (Number.parseFloat(styles.opacity) === 0) return false;

    return element.getClientRects().length > 0;
  };

  const consentDialogIsVisible = () =>
    [...document.querySelectorAll(consentSelector)].some((element) => isVisible(element));

  const currentKlaviyoDialogs = () => {
    const dialogs = new Set();

    document.querySelectorAll(formSelector).forEach((form) => {
      const dialog = form.closest(dialogSelector);
      if (dialog) dialogs.add(dialog);
    });

    return dialogs;
  };

  const currentChatHosts = () => new Set(document.querySelectorAll(chatSelector));

  const suppressChatHosts = () => {
    currentChatHosts().forEach((host) => {
      trackedChatHosts.add(host);
      if (!host.classList.contains(suppressedChatClass)) {
        host.classList.add(suppressedChatClass);
      }
    });

    trackedChatHosts.forEach((host) => {
      if (!host.isConnected) trackedChatHosts.delete(host);
    });
  };

  const suppressDialogs = () => {
    currentKlaviyoDialogs().forEach((dialog) => {
      trackedDialogs.add(dialog);
      if (!dialog.classList.contains(suppressedClass)) {
        dialog.classList.add(suppressedClass);
      }
    });
    suppressChatHosts();

    trackedDialogs.forEach((dialog) => {
      if (!dialog.isConnected) trackedDialogs.delete(dialog);
    });

    if (document.body && document.body.classList.contains(bodyLockClass)) {
      restoreBodyLock = true;
      document.body.classList.remove(bodyLockClass);
    }
  };

  const clearReleaseTimer = () => {
    if (releaseTimer !== null) {
      window.clearTimeout(releaseTimer);
      releaseTimer = null;
    }
  };

  const releaseDialogs = () => {
    const presentDialogs = currentKlaviyoDialogs();
    const dialogsToRelease = new Set([...trackedDialogs, ...presentDialogs]);
    const chatHostsToRelease = new Set([...trackedChatHosts, ...currentChatHosts()]);

    phase = 'released';
    dialogsToRelease.forEach((dialog) => {
      if (dialog.isConnected) dialog.classList.remove(suppressedClass);
    });
    chatHostsToRelease.forEach((host) => {
      if (host.isConnected) host.classList.remove(suppressedChatClass);
    });
    trackedDialogs.clear();
    trackedChatHosts.clear();

    if (restoreBodyLock && presentDialogs.size > 0 && document.body) {
      document.body.classList.add(bodyLockClass);
    }
    restoreBodyLock = false;
  };

  const reconcile = () => {
    const consentVisible = consentDialogIsVisible();

    if (consentVisible) {
      clearReleaseTimer();
      phase = 'blocked';
      suppressDialogs();
      return;
    }

    if (phase === 'initial' || phase === 'blocked') {
      clearReleaseTimer();
      phase = 'waiting';
      suppressDialogs();
      releaseTimer = window.setTimeout(() => {
        releaseTimer = null;

        if (consentDialogIsVisible()) {
          phase = 'blocked';
          suppressDialogs();
          return;
        }

        releaseDialogs();
      }, releaseDelay);
      return;
    }

    if (phase === 'waiting') suppressDialogs();
  };

  const scheduleReconcile = () => {
    if (reconcileQueued) return;
    reconcileQueued = true;

    Promise.resolve().then(() => {
      reconcileQueued = false;
      reconcile();
    });
  };

  if ('MutationObserver' in window) {
    const observer = new MutationObserver(scheduleReconcile);
    observer.observe(document.documentElement, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['class', 'style', 'hidden', 'aria-hidden', 'open']
    });
  }

  document.addEventListener('transitionend', scheduleReconcile, true);
  document.addEventListener('animationend', scheduleReconcile, true);
  scheduleReconcile();
})();
